import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { db } from "../App";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import TagPill from "../components/TagPill";
import ImageGrid from "../components/ImageGrid";

function safeStr(x) {
  return (x || "").toString();
}

function toReadableSentence(text) {
  const t = safeStr(text).trim();
  if (!t) return "";
  if (t.length > 8 && t === t.toUpperCase() && /[A-Z]/.test(t)) {
    const lower = t.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  return t;
}

/** Split body into scope bullets; preserve meaning, prefer sentence case. */
function bodyToScopeBullets(body) {
  const raw = safeStr(body).trim();
  if (!raw) return [];

  let chunks = raw.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  if (chunks.length === 1 && chunks[0].includes("•")) {
    chunks = chunks[0]
      .split(/•/)
      .map((l) => l.replace(/^[-–]\s*/, "").trim())
      .filter(Boolean);
  }
  if (chunks.length === 1) {
    const one = chunks[0];
    const byPeriod = one
      .split(/\.\s+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 12);
    if (byPeriod.length > 1) {
      chunks = byPeriod.map((p) => (p.endsWith(".") ? p : `${p}.`));
    }
  }

  const seen = new Set();
  const out = [];
  for (const c of chunks) {
    const line = toReadableSentence(c);
    if (line && !seen.has(line.toLowerCase())) {
      seen.add(line.toLowerCase());
      out.push(line);
    }
  }
  return out;
}

function splitSummaryParts(summary) {
  const s = safeStr(summary).trim();
  if (!s) return [];
  return s
    .split(/\.\s+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 8)
    .map((p) => (/[.!?]$/.test(p) ? p : `${p}.`));
}

function splitBodyParagraphs(body) {
  return safeStr(body)
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 12);
}

/** Derive challenge / approach / outcome from summary (preferred) or opening lines of body. */
function deriveNarrative(summary, body) {
  const sumParts = splitSummaryParts(summary);
  const bodyParas = splitBodyParagraphs(body);

  let challenge = "";
  let approach = "";
  let outcome = "";

  if (sumParts.length >= 3) {
    challenge = toReadableSentence(sumParts[0]);
    approach = toReadableSentence(sumParts[1]);
    outcome = toReadableSentence(sumParts[2]);
  } else if (sumParts.length === 2) {
    challenge = toReadableSentence(sumParts[0]);
    approach = toReadableSentence(sumParts[1]);
  } else if (sumParts.length === 1) {
    challenge = toReadableSentence(sumParts[0]);
  } else if (bodyParas.length) {
    const opening = safeStr(bodyParas[0]);
    const sentences = opening
      .split(/\.\s+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 20)
      .map((p) => (/[.!?]$/.test(p) ? p : `${p}.`));
    if (sentences[0]) challenge = toReadableSentence(sentences[0]);
    if (sentences[1]) approach = toReadableSentence(sentences[1]);
    if (sentences[2]) outcome = toReadableSentence(sentences[2]);
  }

  const clamp = (s, max) => {
    const t = safeStr(s).trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max).replace(/\s+\S*$/, "").trim()}…`;
  };

  return {
    challenge: clamp(challenge, 420),
    approach: clamp(approach, 420),
    outcome: clamp(outcome, 320),
  };
}

function metadataFromItem(item, title) {
  const tags = (item.tags || []).map((t) => safeStr(t).trim()).filter(Boolean);
  const scopeLike = tags.filter((t) =>
    /\b(renovat|addition|condo|bungalow|interior|exterior|millwork|storey|complete|custom|basement|kitchen|bathroom|floor|residential)\b/i.test(
      t
    )
  );
  const loc = safeStr(item.location).trim();
  return {
    location: loc,
    projectType: tags[0] || title,
    scope: scopeLike.length ? scopeLike.join(", ") : tags.length > 1 ? tags.slice(1).join(", ") : tags[0] || "",
    role: "Design & Construction Consultant",
  };
}

function PortfolioDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const qy = query(
          collection(db, "portfolioItems"),
          where("published", "==", true),
          where("slug", "==", safeStr(slug)),
          limit(1)
        );

        const snap = await getDocs(qy);

        if (snap.empty) {
          if (mounted) setItem(null);
        } else {
          const d = snap.docs[0];
          if (mounted) setItem({ id: d.id, ...d.data() });
        }
      } catch (e) {
        console.error("PortfolioDetail load error:", e);
        if (mounted) setErr(e?.message || "Failed to load portfolio item.");
      }

      if (mounted) setLoading(false);
    }

    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const derived = useMemo(() => {
    if (!item) return null;
    const title = safeStr(item.title) || "Untitled";
    const summary = safeStr(item.summary);
    const body = safeStr(item.body);
    const scopeBullets = bodyToScopeBullets(body);
    const narrative = deriveNarrative(summary, body);
    const overviewLead =
      summary.trim() ||
      (body ? toReadableSentence(safeStr(body).split(/\n\n/)[0]).slice(0, 560) : "");
    const meta = metadataFromItem(item, title);
    return {
      title,
      summary,
      body,
      cover: safeStr(item.coverImageUrl),
      scopeBullets,
      narrative,
      overviewLead,
      meta,
    };
  }, [item]);

  if (loading) {
    return (
      <div className="bg-secondary min-h-[40vh]">
        <div className="mx-auto max-w-6xl px-4 py-8 text-slate-600">Loading…</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="bg-secondary min-h-[40vh]">
        <div className="mx-auto max-w-6xl space-y-3 px-4 py-8">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>
          <Link className="font-medium text-slate-700 underline decoration-slate-300 underline-offset-2" to="/portfolio">
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  if (!item || !derived) {
    return (
      <div className="bg-secondary min-h-[40vh]">
        <div className="mx-auto max-w-6xl space-y-3 px-4 py-8">
          <div className="text-slate-600">Portfolio item not found (or not published).</div>
          <Link className="font-medium text-slate-700 underline decoration-slate-300 underline-offset-2" to="/portfolio">
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  const { title, cover, scopeBullets, narrative, overviewLead, meta } = derived;

  const showChallenge = Boolean(narrative.challenge);
  const showApproach = Boolean(narrative.approach);
  const showOutcome = Boolean(narrative.outcome);
  const showScope = scopeBullets.length > 0;

  return (
    <div className="bg-secondary">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl space-y-3 px-4 py-7 md:py-9">
          <Link
            to="/portfolio"
            className="inline-block text-sm font-medium text-slate-600 underline decoration-slate-300 underline-offset-2 transition hover:text-slate-900"
          >
            ← Back to Portfolio
          </Link>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{title}</h1>
          <div className="flex flex-wrap gap-2 pt-1">
            {(item.tags || []).map((t, idx) => (
              <TagPill key={`${t}-${idx}`}>{safeStr(t)}</TagPill>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl space-y-7 px-4 py-8 md:space-y-9 md:py-10">
        {/* 1. Project overview */}
        <section className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 md:items-start">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Project overview</h2>
            {overviewLead ? (
              <p className="mt-2.5 text-slate-700 leading-relaxed">{toReadableSentence(overviewLead)}</p>
            ) : (
              <p className="mt-2.5 text-slate-700 leading-relaxed">
                Residential project documented below with imagery and scope notes.
              </p>
            )}
          </div>
          <dl className="space-y-2.5 rounded-xl border border-slate-200/80 bg-white p-4 text-sm shadow-sm ring-1 ring-slate-200/40 md:max-w-md md:justify-self-end">
            {meta.location ? (
              <div>
                <dt className="font-semibold text-slate-900">Location</dt>
                <dd className="mt-0.5 text-slate-700 leading-relaxed">{meta.location}</dd>
              </div>
            ) : null}
            <div>
              <dt className="font-semibold text-slate-900">Project type</dt>
              <dd className="mt-0.5 text-slate-700 leading-relaxed">{toReadableSentence(meta.projectType)}</dd>
            </div>
            {meta.scope ? (
              <div>
                <dt className="font-semibold text-slate-900">Scope</dt>
                <dd className="mt-0.5 text-slate-700 leading-relaxed">{meta.scope}</dd>
              </div>
            ) : null}
            <div>
              <dt className="font-semibold text-slate-900">Role</dt>
              <dd className="mt-0.5 text-slate-700 leading-relaxed">{meta.role}</dd>
            </div>
          </dl>
        </section>

        {/* Hero image (unchanged treatment) */}
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-200/40">
          <div className="mx-auto max-w-5xl">
            <div className="relative aspect-[16/9] w-full bg-slate-100">
              {cover ? (
                <img src={cover} alt={title} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400">No cover image</div>
              )}
            </div>
          </div>
        </div>

        {showChallenge ? (
          <section className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-900">The challenge</h2>
            <p className="mt-2.5 text-slate-700 leading-relaxed">{narrative.challenge}</p>
          </section>
        ) : null}

        {showApproach ? (
          <section className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Approach</h2>
            <p className="mt-2.5 text-slate-700 leading-relaxed">{narrative.approach}</p>
          </section>
        ) : null}

        {showOutcome ? (
          <section className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Outcome</h2>
            <p className="mt-2.5 text-slate-700 leading-relaxed">{narrative.outcome}</p>
          </section>
        ) : null}

        {showScope ? (
          <section className="max-w-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Scope of work</h2>
            <ul className="mt-2.5 list-disc space-y-1.5 pl-5 text-slate-700 leading-relaxed marker:text-slate-400">
              {scopeBullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Gallery — unchanged structure and behavior */}
        <div className="space-y-3">
          <div className="flex flex-col gap-1 border-b border-slate-200/80 pb-2.5 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="text-xl font-bold text-slate-900">Gallery</h2>
            <p className="text-sm text-slate-500">Click any photo to enlarge</p>
          </div>

          <ImageGrid images={item.gallery || []} />

          {!Array.isArray(item.gallery) || item.gallery.length === 0 ? (
            <p className="text-sm text-slate-500">No gallery images.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PortfolioDetail;
